import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { Dialog, Grid } from '@material-ui/core';
import { directAffinityCodes } from '@ourbranch/lookups';
import DialogContent from '@material-ui/core/DialogContent';

import { SearchBar } from 'core/components/search-bar';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { ActionButton } from 'core/components/action-button';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store/';
import useSession from 'core/hooks/use-session';
import { FormField } from 'core/components/form';
import { Tooltip } from 'core/components/tooltip';
import { track } from 'core/helpers/analytics';
import { setTouchedFromErrors } from 'core/components/validation-errors-modal/validation-errors-modal';
import { isCanonicalUser } from 'core/helpers/troubleshooting-mode';
import x from 'core/assets/svg/x.svg';
import CustomersResults from 'common/components/search-results/customers-results';
import useStyles from './search-dialog.styles';

const CustomerSearchDialog = observer(({ setShowValidationErrorsModal }) => {
  const classes = useStyles();
  const {
    matchSearch: {
      showCustomerMatchesModal,
      customersResults,
      clearInput,
      searchCustomersAction,
      searchOffersAction,
      currentResultsPage,
      setCurrentResultsPage,
      setShowCustomerMatches,
      loading,
      searchString,
      setSearchString
    },
    offer: { offer, loading: offerLoading },
    account: { sendEventAsMember },
    affinityLookups
  } = useStore();
  const history = useHistory();
  const toast = useToast();
  const session = useSession();
  const { user } = session;

  const [userSearching, setUserSearching] = useState(false);

  const { handleSubmit, values, setFieldValue, setFieldTouched, setFieldError, validateForm, setErrors, setTouched } =
    useFormikContext();

  const onLinkingExistingMember = async () => {
    if (values?.accountIdMatchField?.toString().length !== 9) {
      const accountIdMatchField = values?.accountIdMatchField?.toString().length;
      const message =
        accountIdMatchField < 9 ? 'Account ID must be 9 digits.' : "Please enter the member's account ID to continue.";
      toast.notify({
        type: 'error',
        message: message,
        durationInSeconds: 15,
        label: 'X',
        action: () => {}
      });
      setFieldError('accountIdMatchField', "Please enter the member's account ID to continue.");
      return;
    }
    // Linking submits the whole offer form; surface outer-form errors instead of
    // letting Formik swallow the submit (silent dead click)
    const formErrors = await validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setTouched(setTouchedFromErrors(formErrors), false);
      setShowCustomerMatches(false);
      setShowValidationErrorsModal(true);
      track('Failed to Link offer to existing member', {
        user: user.username,
        offerId: offer.id,
        selectedAccountId: values.existingCustomer?.id,
        reason: 'offer form validation errors'
      });
      return;
    }

    if (parseInt(values.existingCustomer?.id, 10) !== values.accountIdMatchField) {
      toast.notify({
        type: 'error',
        message:
          'The account ID entered does not match the member you are trying to link the offer to. Please try again.',
        durationInSeconds: 15,
        label: 'X',
        action: () => {}
      });

      track('Failed to Link offer to existing member', {
        user: user.username,
        offerId: offer.id,
        inputAccountId: values.accountIdMatchField,
        selectedAccountId: values.existingCustomer?.id
      });
      return;
    }

    const linkedMember = customersResults.hits.find((h) => h.objectID === values.existingCustomer.id);
    const linkedMemberAffinity = linkedMember?.attributes?.custom_affinity_code;
    // exclude directAffinityCodes
    if (!offer.quote.global.affinity && linkedMemberAffinity && !directAffinityCodes.includes(linkedMemberAffinity)) {
      await affinityLookups.getByAffinity(linkedMemberAffinity);
      const isDisabledAffinity = affinityLookups.data?.find(
        ({ affinity }) => affinity === linkedMemberAffinity
      )?.disabled;

      // don't carry over a disabled affinity onto the offer
      if (!isDisabledAffinity) {
        setFieldValue('global.affinity', linkedMemberAffinity);
        setFieldTouched('global.affinity', true);
      }
    }

    track('Linked offer to existing member', {
      user: user.username,
      offerId: offer.id,
      selectedAccountId: values.existingCustomer?.id
    });
    await sendEventAsMember({
      eventName: 'Linked offer to existing member - Applicant',
      userId: offer?.webUserId,
      properties: JSON.stringify({
        user: user.username,
        offerId: offer.id,
        selectedAccountId: values.existingCustomer?.id
      })
    });
    handleSubmit();
  };

  // Set initial search string only when modal opens and search is empty
  if (offer && !offerLoading && !loading && showCustomerMatchesModal && !searchString) {
    let expectedSearchString;

    if (offer.quote?.existingCustomer?.id) {
      expectedSearchString = offer.quote.existingCustomer.id;
    } else if (offer.quote?.fname && offer.quote?.lname) {
      const offerState = offer?.state || offer?.quote?.correctedAddress?.state || offer?.quote?.address?.state || '';
      const state = offerState ? ` ${offerState}` : '';
      expectedSearchString = `${offer.quote.fname} ${offer.quote.lname}${state}`;
    }

    if (expectedSearchString && !userSearching) {
      setCurrentResultsPage(0);
      setSearchString(expectedSearchString);
      searchCustomersAction();
      searchOffersAction({ excludeTroubleshootingOffers: isCanonicalUser(session) });
    }
  }

  useEffect(() => {
    searchCustomersAction();
  }, [searchString, searchCustomersAction]);

  const onSearchStringChange = useCallback(
    (event) => {
      setSearchString(event.target.value);
      setUserSearching(true);
    },
    [setSearchString]
  );

  const onPageChange = useCallback(
    (page) => {
      setCurrentResultsPage(page);
      searchCustomersAction(searchString);
    },
    [searchString, setCurrentResultsPage, searchCustomersAction]
  );

  const onClose = () => {
    setFieldValue('accountIdMatchField', null);
    setFieldValue('existingCustomer', null);
    setShowCustomerMatches(false);
  };

  return (
    <Dialog maxWidth="lg" fullWidth open={showCustomerMatchesModal} onClose={onClose}>
      <DialogContent className={classes.searchDialog}>
        <Suspense fallback={null}>
          <Grid container>
            <Grid item xs={12} className={classes.searchHeader}>
              <Label type="greenBig">{customersResults.nbHits} Existing members</Label>
              <Label type="infoValue" className={classes.searchSubtitle}>
                Select a match to link to member or search more
              </Label>
            </Grid>
            <Grid item xs={7} className={classes.searchBar}>
              <SearchBar
                onCloseIconClick={clearInput}
                onInputChange={onSearchStringChange}
                value={searchString}
                label="Search"
              />
            </Grid>
          </Grid>
          <CustomersResults
            loading={loading}
            hits={customersResults.hits}
            totalHits={customersResults.nbHits}
            page={currentResultsPage}
            onPageChange={onPageChange}
            history={history}
            mode="modal"
            showTitle={false}
            columnsToUse={['select', 'fname', 'lname', 'address', 'city', 'state', 'zip']}
            selectFieldName="existingCustomer.id"
          />
          <Grid container alignItems="center" justifyContent="flex-end" className={classes.searchFooter}>
            <Grid item className={classes.accountIdInput}>
              <FormField
                name="accountIdMatchField"
                type="numeric"
                label="Account ID"
                mode="light"
                format={{
                  allowNegative: false
                }}
                onInput={(e) => {
                  e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 9);
                }}
              />
              <div className={classes.accountIdTooltipContainer}>
                <Tooltip
                  text="The account ID is the first 9 digits of the policy number; this number can also be found on the member's dec page."
                  placement="top"
                  label="More info"
                  className={classes.accountIdTooltip}
                />
              </div>
            </Grid>
            <Grid item className={classes.buttonContainer}>
              <Button
                className={classes.linkButton}
                type="submit"
                disabled={loading || offerLoading || !values.existingCustomer?.id}
                loading={loading || offerLoading}
                variant="contained"
                color="primary"
                onClick={onLinkingExistingMember}
              >
                <Label>Link To Existing Member</Label>
              </Button>
            </Grid>
          </Grid>
        </Suspense>
        <ActionButton size="big" type="edit" icon={x} className={classes.closeButton} onClick={onClose} />
      </DialogContent>
    </Dialog>
  );
});
export default CustomerSearchDialog;
